/**
 * Diálogos de la app en lugar de los nativos del navegador.
 *
 * `window.confirm` / `window.alert` bloquean el hilo, no se pueden estilar, no
 * se traducen (los botones salen en el idioma del navegador, no en el de la
 * app) y rompen el aspecto del producto delante del cliente. Aquí se sustituyen
 * por dos primitivas con el estilo de JR2 OS.
 *
 * La API es IMPERATIVA a propósito, no un hook: hay ~100 puntos de llamada
 * repartidos por todo el front y muchos están dentro de callbacks, handlers
 * anidados o funciones auxiliares fuera del cuerpo del componente, donde un
 * hook no se puede llamar. Así la sustitución es textual:
 *
 *   if (!confirm(msg)) return          →  if (!(await confirmDialog(msg))) return
 *   alert(msg)                         →  toast.error(msg)
 *   const v = prompt(msg, def)         →  const v = await promptDialog(msg, def)
 *
 * El host (`DialogHost`) se monta UNA vez en main.tsx y registra aquí sus
 * funciones. Si por lo que sea no estuviera montado, se cae al diálogo nativo:
 * es preferible un modal feo a perder una confirmación de borrado.
 */

export type ConfirmTone = 'default' | 'danger'

export interface ConfirmOptions {
  message: string
  title?: string
  confirmLabel?: string
  cancelLabel?: string
  /** 'danger' pinta el botón de confirmar en rojo (borrados, acciones irreversibles). */
  tone?: ConfirmTone
}

type OpenConfirm = (opts: ConfirmOptions) => Promise<boolean>

let openConfirm: OpenConfirm | null = null

/** Lo llama DialogHost al montarse; null al desmontarse. */
export function registerConfirmHost(fn: OpenConfirm | null): void {
  openConfirm = fn
}

/**
 * Sustituto de `window.confirm`. Acepta el mensaje suelto (caso mayoritario) o
 * un objeto con título/etiquetas/tono.
 */
export function confirmDialog(input: ConfirmOptions | string): Promise<boolean> {
  const opts: ConfirmOptions = typeof input === 'string' ? { message: input } : input
  if (!openConfirm) return Promise.resolve(window.confirm(opts.message))
  return openConfirm(opts)
}

export interface PromptOptions {
  message: string
  title?: string
  /** Valor precargado y seleccionado al abrir, como el 2º argumento de window.prompt. */
  defaultValue?: string
  placeholder?: string
  confirmLabel?: string
  cancelLabel?: string
}

type OpenPrompt = (opts: PromptOptions) => Promise<string | null>

let openPrompt: OpenPrompt | null = null

/** Lo llama DialogHost al montarse; null al desmontarse. */
export function registerPromptHost(fn: OpenPrompt | null): void {
  openPrompt = fn
}

/**
 * Sustituto de `window.prompt`. Devuelve el texto introducido o `null` si el
 * usuario cancela, igual que el nativo (una cadena vacía sigue siendo falsy,
 * así que los `if (!valor) return` de los call sites siguen valiendo).
 */
export function promptDialog(
  input: PromptOptions | string,
  defaultValue?: string
): Promise<string | null> {
  const opts: PromptOptions =
    typeof input === 'string' ? { message: input, defaultValue } : input
  if (!openPrompt) return Promise.resolve(window.prompt(opts.message, opts.defaultValue ?? ''))
  return openPrompt(opts)
}

export type ToastVariant = 'error' | 'success' | 'info'

export interface ToastItem {
  id: number
  message: string
  variant: ToastVariant
}

type PushToast = (message: string, variant: ToastVariant) => void

let pushToast: PushToast | null = null

/** Lo llama DialogHost al montarse; null al desmontarse. */
export function registerToastHost(fn: PushToast | null): void {
  pushToast = fn
}

function emit(message: unknown, variant: ToastVariant): void {
  // Muchos call sites pasan `err.message ?? body?.error`, que puede llegar
  // undefined: un aviso vacío es peor que ninguno.
  const text = typeof message === 'string' ? message.trim() : String(message ?? '').trim()
  if (!text) return
  if (!pushToast) {
    window.alert(text)
    return
  }
  pushToast(text, variant)
}

/** Sustituto de `window.alert`, con variantes. */
export const toast = {
  error: (message: unknown) => emit(message, 'error'),
  success: (message: unknown) => emit(message, 'success'),
  info: (message: unknown) => emit(message, 'info'),
}
